import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  PageShell,
  PageHeader,
  EmptyState,
  Modal,
} from "../../../Components/shared";
import {
  FaMapMarkedAlt,
  FaPlus,
  FaTabletAlt,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import { useAlert } from "../../../context/AlertContext";
import {
  useGetFactoryZonesQuery,
  useGetFactoryKiosksQuery,
  useCreateFactoryZoneMutation,
  useUpdateFactoryZoneMutation,
  useDeleteFactoryZoneMutation,
  useGetFactoryLayoutItemsQuery,
  useCreateFactoryLayoutItemMutation,
  useUpdateFactoryLayoutItemMutation,
  useDeleteFactoryLayoutItemMutation,
} from "../../../store";
import RowActionButton from "../../../Components/factory/storageRoles/RowActionButton";
import FactoryMap, {
  ZONE_COLORS,
  ZONE_COLOR_KEYS,
} from "../../../Components/factory/storageRoles/FactoryMap";

const ONLINE_WINDOW_MS = 5 * 60 * 1000;

const formatDate = (iso) => {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Fabrika Bölgeler & Tabletler — bölge yönetimi + kiosk heartbeat listesi. */
export default function FactoryZonesPage() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { hasPermission } = usePermission();
  const canManage = hasPermission("factory.zones.manage");
  const { showSuccess, showError } = useAlert();

  const { data: zones = [], isLoading: zonesLoading } = useGetFactoryZonesQuery(
    { token },
    { skip: !token }
  );
  const { data: kiosks = [], isLoading: kiosksLoading } = useGetFactoryKiosksQuery(
    { token },
    { pollingInterval: 60_000, skip: !token }
  );

  const { data: layoutItems = [] } = useGetFactoryLayoutItemsQuery(
    { token },
    { skip: !token }
  );
  const [createLayoutItem] = useCreateFactoryLayoutItemMutation();
  const [updateLayoutItem] = useUpdateFactoryLayoutItemMutation();
  const [deleteLayoutItem] = useDeleteFactoryLayoutItemMutation();

  const [createZone, { isLoading: creating }] = useCreateFactoryZoneMutation();
  const [updateZone, { isLoading: updating }] = useUpdateFactoryZoneMutation();
  const [deleteZone] = useDeleteFactoryZoneMutation();

  // Modal state: null = kapalı, {} = yeni, {zone} = düzenle
  const [editing, setEditing] = useState(null);
  const [nameDraft, setNameDraft] = useState("");
  const [colorDraft, setColorDraft] = useState("emerald");
  // Krokiden çizilen yeni alanın koordinatları (isim girilince zone'a yazılır)
  const [pendingRect, setPendingRect] = useState(null);

  const loading = zonesLoading || kiosksLoading;

  const kiosksByZone = useMemo(() => {
    const map = {};
    kiosks.forEach((k) => {
      const zoneId = k.zone?._id || "unassigned";
      if (!map[zoneId]) map[zoneId] = [];
      map[zoneId].push(k);
    });
    return map;
  }, [kiosks]);

  const isOnline = (k) =>
    k.lastSeenAt && Date.now() - new Date(k.lastSeenAt).getTime() < ONLINE_WINDOW_MS;

  const openCreate = () => {
    setNameDraft("");
    setColorDraft("emerald");
    setPendingRect(null);
    setEditing({});
  };

  const openEdit = (zone) => {
    setNameDraft(zone.name);
    setColorDraft(zone.color || "emerald");
    setPendingRect(null);
    setEditing({ zone });
  };

  /** Krokide boş alana çizilen dikdörtgen → isim sorulup bölge oluşturulur */
  const handleMapDraw = (rect) => {
    setNameDraft("");
    setColorDraft(rect.color || "emerald");
    setPendingRect(rect);
    setEditing({});
  };

  // Kroki yapı öğeleri (duvar/kapı/zemin) — hata olursa sessiz kalmasın
  const handleCreateItem = async (payload) => {
    try {
      await createLayoutItem({ token, ...payload }).unwrap();
    } catch (err) {
      showError(err?.data?.message || "Öğe eklenemedi");
    }
  };

  const handleUpdateItem = async (item, patch) => {
    try {
      await updateLayoutItem({ token, id: item._id, ...patch }).unwrap();
    } catch (err) {
      showError(err?.data?.message || "Öğe güncellenemedi");
    }
  };

  const handleDeleteItem = async (item) => {
    try {
      await deleteLayoutItem({ token, id: item._id }).unwrap();
    } catch (err) {
      showError(err?.data?.message || "Öğe silinemedi");
    }
  };

  /** Krokide sürükle-bırak → koordinat güncelle */
  const handleMapMove = async (zone, coords) => {
    try {
      await updateZone({ token, id: zone._id, ...coords }).unwrap();
    } catch (err) {
      showError(err?.data?.message || "Taşınamadı");
    }
  };

  const closeModal = () => {
    setEditing(null);
    setPendingRect(null);
  };

  const submit = async () => {
    try {
      if (editing?.zone) {
        await updateZone({
          token,
          id: editing.zone._id,
          name: nameDraft,
          color: colorDraft,
        }).unwrap();
        showSuccess("Bölge güncellendi");
      } else {
        await createZone({
          token,
          name: nameDraft,
          ...(pendingRect || {}),
          color: colorDraft,
        }).unwrap();
        showSuccess("Bölge oluşturuldu");
      }
      closeModal();
    } catch (err) {
      showError(err?.data?.message || "İşlem başarısız");
    }
  };

  const toggleActive = async (zone) => {
    try {
      await updateZone({ token, id: zone._id, isActive: !zone.isActive }).unwrap();
      showSuccess(zone.isActive ? "Bölge pasife alındı" : "Bölge aktifleştirildi");
    } catch (err) {
      showError(err?.data?.message || "İşlem başarısız");
    }
  };

  // Kalıcı silme — geçmişi olan bölgede server 422 döner (mesajı gösteririz)
  const [deleteTarget, setDeleteTarget] = useState(null);
  const submitDelete = async () => {
    try {
      await deleteZone({ token, id: deleteTarget._id }).unwrap();
      showSuccess("Bölge silindi");
      setDeleteTarget(null);
    } catch (err) {
      setDeleteTarget(null);
      showError(err?.data?.message || "Bölge silinemedi");
    }
  };

  const onlineCount = kiosks.filter(isOnline).length;

  // ── Yerleşim durumu ────────────────────────────────────────────────────
  // Bölge listesi sağda açılır kapanır panelde (eskiden krokinin altında tam
  // genişlikte tabloydu; kroki için ekranda yer bırakmıyordu).
  const [panelOpen, setPanelOpen] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  /** Araç çubuğunun basılacağı düğüm — başlık şeridindeki boşluk. */
  const [toolbarEl, setToolbarEl] = useState(null);

  // Tam ekrandan Esc ile çıkış (tarayıcı tam ekranı değil, sayfa içi katman).
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen]);

  // Kroki TEK yerde kurulur; tam ekranda aynı düğüm katmanın içine taşınır.
  // Tam ekranda araç çubuğu krokinin üstünde kalır (yer bol), normalde başlığa
  // taşınır — bu yüzden toolbarSlot yalnız normal modda verilir.
  const mapNode = (
    <FactoryMap
      zones={zones}
      layoutItems={layoutItems}
      kiosksByZone={kiosksByZone}
      canManage={canManage}
      fill
      toolbarSlot={fullscreen ? null : toolbarEl}
      fullscreen={fullscreen}
      onToggleFullscreen={() => setFullscreen((v) => !v)}
      onDrawZone={handleMapDraw}
      onSelectZone={openEdit}
      onEditZone={openEdit}
      onDeleteZone={(zone) => setDeleteTarget(zone)}
      onMoveZone={handleMapMove}
      onCreateItem={handleCreateItem}
      onUpdateItem={handleUpdateItem}
      onDeleteItem={handleDeleteItem}
    />
  );

  return (
    <PageShell>
      {/* Başlık şeridi — araç çubuğu istatistiklerin SOLUNDA (slot ile). */}
      <div className="px-3 py-2.5 sm:px-4 sm:py-3 border-b border-gray-100 shrink-0">
        <PageHeader
          icon={FaMapMarkedAlt}
          iconColor="blue"
          title="Bölgeler & Tabletler"
          subtitle="Üretim bölgeleri ve kiosk tablet durumu"
          slot={
            !fullscreen ? (
              <div ref={setToolbarEl} className="flex items-center gap-2 mr-auto sm:mr-2" />
            ) : null
          }
          stats={[
            { label: "Bölge", value: zones.filter((z) => z.isActive).length },
            { label: "Çevrimiçi Tablet", value: `${onlineCount}/${kiosks.length}` },
          ]}
          isLoading={loading}
          actions={
            canManage
              ? [
                  {
                    label: "Yeni Bölge",
                    icon: FaPlus,
                    variant: "primary",
                    onClick: openCreate,
                  },
                ]
              : []
          }
        />
      </div>

      {/* Gövde: solda kroki (kalan tüm alanı kaplar), sağda açılır kapanır liste */}
      <div className="flex-1 min-h-0 flex">
        <div className="flex-1 min-w-0 p-2.5 sm:p-3">
          {fullscreen ? (
            <div className="h-full flex items-center justify-center rounded-xl border border-dashed border-gray-200 text-sm text-gray-400">
              Kroki tam ekranda açık
            </div>
          ) : (
            mapNode
          )}
        </div>

        <aside
          className={`shrink-0 border-l border-gray-200 bg-gray-50/60 flex flex-col transition-[width] duration-200 ${
            panelOpen ? "w-[300px] lg:w-[340px]" : "w-9"
          }`}
        >
          <button
            type="button"
            onClick={() => setPanelOpen((v) => !v)}
            title={panelOpen ? "Listeyi gizle" : "Listeyi göster"}
            className={`shrink-0 flex items-center gap-2 text-xs font-semibold text-gray-500 hover:bg-gray-100 transition-colors ${
              panelOpen ? "px-3 py-2.5 border-b border-gray-200" : "flex-col justify-center h-full py-3"
            }`}
          >
            {panelOpen ? (
              <>
                <FaChevronRight className="w-3 h-3" />
                <span>Bölge Listesi</span>
                <span className="ml-auto text-gray-400">{zones.length}</span>
              </>
            ) : (
              <>
                <FaChevronLeft className="w-3 h-3" />
                <span className="[writing-mode:vertical-rl] tracking-wide">Bölge Listesi</span>
              </>
            )}
          </button>

          {panelOpen && (
            <div className="flex-1 min-h-0 overflow-y-auto p-2.5 space-y-2">
              {loading && <p className="text-xs text-gray-400 px-1">Yükleniyor…</p>}
              {!loading && zones.length === 0 && (
                <EmptyState
                  message="Bölge tanımlı değil"
                  suggestion={canManage ? "Yeni Bölge ile ekleyin" : undefined}
                />
              )}
              {zones.map((zone) => {
                const zoneKiosks = kiosksByZone[zone._id] || [];
                return (
                  <div
                    key={zone._id}
                    className="bg-white border border-gray-200 rounded-xl p-2.5 hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-sm font-semibold text-gray-900 leading-snug">
                        {zone.name}
                      </span>
                      <span
                        className={`shrink-0 inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                          zone.isActive
                            ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                            : "bg-gray-50 text-gray-500 border-gray-100"
                        }`}
                      >
                        {zone.isActive ? "Aktif" : "Pasif"}
                      </span>
                    </div>

                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {zoneKiosks.length === 0 ? (
                        <span className="text-[11px] text-gray-400">Tablet yok</span>
                      ) : (
                        zoneKiosks.map((k) => (
                          <span
                            key={k._id}
                            title={`${k.deviceUuid} · son görülme: ${formatDate(k.lastSeenAt)}`}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                              isOnline(k)
                                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                : "bg-gray-50 text-gray-500 border-gray-100"
                            }`}
                          >
                            <FaTabletAlt className="w-2.5 h-2.5" />
                            {k.deviceUuid?.slice(0, 8)}
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                isOnline(k) ? "bg-emerald-500" : "bg-gray-300"
                              }`}
                            />
                          </span>
                        ))
                      )}
                    </div>

                    {canManage && (
                      <div className="mt-2 flex flex-wrap items-center gap-1">
                        <RowActionButton variant="primary" onClick={() => openEdit(zone)}>
                          Düzenle
                        </RowActionButton>
                        <RowActionButton
                          variant={zone.isActive ? "warning" : "success"}
                          onClick={() => toggleActive(zone)}
                        >
                          {zone.isActive ? "Pasife Al" : "Aktifleştir"}
                        </RowActionButton>
                        <RowActionButton variant="danger" onClick={() => setDeleteTarget(zone)}>
                          Sil
                        </RowActionButton>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </aside>
      </div>

      {/* Tam ekran — krokiyi sayfadan koparıp tüm pencereye yayar (Esc ile çıkar).
          Araç çubuğu burada krokinin üstünde durur; başlığa taşımaya gerek yok. */}
      {fullscreen &&
        createPortal(
          <div className="fixed inset-0 z-[100] bg-white flex flex-col">
            <div className="px-4 py-2.5 border-b border-gray-200 flex items-center gap-3 shrink-0">
              <FaMapMarkedAlt className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-bold text-gray-900">Fabrika Krokisi</span>
              <span className="text-xs text-gray-400 hidden sm:inline">
                Çıkmak için Esc
              </span>
            </div>
            <div className="flex-1 min-h-0 p-3">{mapNode}</div>
          </div>,
          document.body
        )}

      {/* Silme onayı — geçmişi olan bölgeyi server reddeder (422) */}
      <Modal open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="md">
        <Modal.Header
          title="Bölgeyi Sil"
          subtitle={deleteTarget?.name}
          onClose={() => setDeleteTarget(null)}
        />
        <Modal.Body>
          <p className="text-sm text-gray-600">
            Bölge kalıcı olarak silinecek ve krokiden kalkacak. Bu bölgede
            geçmiş kayıt (işlem, üretim) varsa silme reddedilir — o durumda
            "Pasife Al" kullanın. Atanmış tabletler bölgesiz kalır ve
            mini-admin'den yeniden atanması gerekir.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            onClick={() => setDeleteTarget(null)}
            className="px-3.5 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Vazgeç
          </button>
          <button
            onClick={submitDelete}
            className="px-3.5 py-2 text-sm font-semibold text-white bg-fifth rounded-lg hover:opacity-90"
          >
            Kalıcı Olarak Sil
          </button>
        </Modal.Footer>
      </Modal>

      <Modal open={Boolean(editing)} onClose={closeModal} maxWidth="md">
        <Modal.Header
          title={editing?.zone ? "Bölgeyi Düzenle" : "Yeni Bölge"}
          subtitle={pendingRect ? "Krokide çizilen alan bu isimle kaydedilecek" : undefined}
          onClose={closeModal}
        />
        <Modal.Body>
          <label className="block text-xs font-semibold text-gray-500 mb-1">
            Bölge Adı
          </label>
          <input
            type="text"
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            placeholder="Örn. Bölge 5"
            className="w-full border border-input rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
          />
          <label className="block text-xs font-semibold text-gray-500 mt-3 mb-1.5">
            Kroki Rengi
          </label>
          <div className="flex items-center gap-2">
            {ZONE_COLOR_KEYS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColorDraft(c)}
                className={`w-7 h-7 rounded-lg ${ZONE_COLORS[c].chip} ${
                  colorDraft === c ? "ring-2 ring-offset-1 ring-gray-500" : ""
                }`}
              />
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            onClick={closeModal}
            className="px-3.5 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Vazgeç
          </button>
          <button
            onClick={submit}
            disabled={!nameDraft.trim() || creating || updating}
            className="px-3.5 py-2 text-sm font-semibold text-white bg-main rounded-lg hover:bg-mainSecond disabled:opacity-50"
          >
            {editing?.zone ? "Kaydet" : "Oluştur"}
          </button>
        </Modal.Footer>
      </Modal>
    </PageShell>
  );
}
